import type { ReactElement } from 'react';

type StepId = 'account' | 'formation' | 'payment' | 'follow';

const PRIMARY = '#872320';
const PRIMARY_LIGHT = '#f3e4e3';
const MUTED = '#94a3b8';
const INK = '#1e293b';
const SOFT = '#e8edf2';

type Props = {
  step: StepId;
  className?: string;
};

function AccountIllustration() {
  return (
    <svg viewBox="0 0 320 200" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
      <rect width="320" height="200" rx="16" fill={SOFT} />
      <circle cx="72" cy="88" r="34" fill="#fff" stroke={PRIMARY_LIGHT} strokeWidth="2" />
      <circle cx="72" cy="78" r="14" fill={PRIMARY} opacity="0.85" />
      <path d="M42 108c6-14 20-22 30-22s24 8 30 22" stroke={PRIMARY} strokeWidth="3" strokeLinecap="round" />
      <rect x="128" y="52" width="156" height="96" rx="12" fill="#fff" stroke={PRIMARY_LIGHT} strokeWidth="2" />
      <rect x="144" y="72" width="88" height="10" rx="5" fill={PRIMARY_LIGHT} />
      <rect x="144" y="92" width="120" height="8" rx="4" fill="#dce4ec" />
      <rect x="144" y="108" width="72" height="8" rx="4" fill="#dce4ec" />
      <rect x="144" y="124" width="48" height="12" rx="6" fill={PRIMARY} opacity="0.9" />
      <text x="152" y="133" fill="#fff" fontSize="8" fontWeight="700" fontFamily="system-ui,sans-serif">
        OTP
      </text>
      <circle cx="268" cy="64" r="18" fill={PRIMARY_LIGHT} />
      <path
        d="M262 64l4 4 8-8"
        stroke={PRIMARY}
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function FormationIllustration() {
  return (
    <svg viewBox="0 0 320 200" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
      <rect width="320" height="200" rx="16" fill={SOFT} />
      <rect x="36" y="44" width="108" height="112" rx="10" fill="#fff" stroke={PRIMARY_LIGHT} strokeWidth="2" />
      <rect x="52" y="60" width="76" height="52" rx="6" fill={PRIMARY_LIGHT} />
      <circle cx="90" cy="82" r="14" fill="#fff" opacity="0.9" />
      <rect x="52" y="122" width="48" height="6" rx="3" fill="#dce4ec" />
      <rect x="52" y="134" width="64" height="6" rx="3" fill="#dce4ec" />
      <rect x="164" y="56" width="120" height="100" rx="12" fill="#fff" stroke={PRIMARY_LIGHT} strokeWidth="2" />
      <rect x="180" y="72" width="88" height="10" rx="5" fill={PRIMARY} opacity="0.15" />
      <rect x="180" y="92" width="72" height="8" rx="4" fill="#dce4ec" />
      <rect x="180" y="108" width="80" height="8" rx="4" fill="#dce4ec" />
      <rect x="180" y="128" width="56" height="16" rx="8" fill={PRIMARY} opacity="0.85" />
      <path
        d="M148 100h16"
        stroke={MUTED}
        strokeWidth="2"
        strokeLinecap="round"
        strokeDasharray="4 4"
      />
      <circle cx="156" cy="100" r="10" fill="#fff" stroke={PRIMARY} strokeWidth="2" />
      <path d="M152 100l3 3 6-6" stroke={PRIMARY} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function PaymentIllustration() {
  return (
    <svg viewBox="0 0 320 200" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
      <rect width="320" height="200" rx="16" fill={SOFT} />
      <rect x="88" y="48" width="144" height="104" rx="14" fill="#fff" stroke={PRIMARY_LIGHT} strokeWidth="2" />
      <rect x="104" y="64" width="112" height="16" rx="8" fill={PRIMARY} opacity="0.85" />
      <rect x="104" y="92" width="80" height="8" rx="4" fill="#dce4ec" />
      <rect x="104" y="108" width="96" height="8" rx="4" fill="#dce4ec" />
      <rect x="104" y="124" width="64" height="16" rx="8" fill={PRIMARY_LIGHT} />
      <rect x="48" y="72" width="40" height="72" rx="10" fill={INK} opacity="0.9" />
      <rect x="54" y="80" width="28" height="44" rx="4" fill="#fff" opacity="0.95" />
      <circle cx="68" cy="132" r="4" fill="#fff" opacity="0.5" />
      <path
        d="M58 98h20M58 106h14"
        stroke={PRIMARY}
        strokeWidth="2"
        strokeLinecap="round"
        opacity="0.7"
      />
      <circle cx="248" cy="100" r="28" fill={PRIMARY_LIGHT} />
      <text x="238" y="104" fill={PRIMARY} fontSize="14" fontWeight="800" fontFamily="system-ui,sans-serif">
        CDF
      </text>
    </svg>
  );
}

function FollowIllustration() {
  return (
    <svg viewBox="0 0 320 200" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
      <rect width="320" height="200" rx="16" fill={SOFT} />
      <rect x="40" y="44" width="240" height="112" rx="12" fill="#fff" stroke={PRIMARY_LIGHT} strokeWidth="2" />
      <rect x="56" y="60" width="72" height="80" rx="8" fill={PRIMARY_LIGHT} opacity="0.55" />
      <rect x="64" y="72" width="24" height="6" rx="3" fill={PRIMARY} opacity="0.5" />
      <rect x="64" y="86" width="40" height="6" rx="3" fill="#dce4ec" />
      <rect x="64" y="100" width="36" height="6" rx="3" fill="#dce4ec" />
      <rect x="64" y="114" width="44" height="6" rx="3" fill="#dce4ec" />
      <rect x="140" y="60" width="124" height="36" rx="8" fill={PRIMARY_LIGHT} />
      <path
        d="M152 88h88M152 76h56"
        stroke={PRIMARY}
        strokeWidth="2"
        strokeLinecap="round"
        opacity="0.35"
      />
      <rect x="140" y="104" width="56" height="36" rx="8" fill="#fff" stroke={PRIMARY_LIGHT} strokeWidth="1.5" />
      <rect x="204" y="104" width="60" height="36" rx="8" fill="#fff" stroke={PRIMARY_LIGHT} strokeWidth="1.5" />
      <path d="M148 122h36M212 122h40" stroke="#dce4ec" strokeWidth="6" strokeLinecap="round" />
      <circle cx="252" cy="56" r="14" fill={PRIMARY} opacity="0.9" />
      <path d="M246 56l4 4 8-8" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

const ILLUSTRATIONS: Record<StepId, () => ReactElement> = {
  account: AccountIllustration,
  formation: FormationIllustration,
  payment: PaymentIllustration,
  follow: FollowIllustration,
};

export default function PublicInscriptionStepIllustration({ step, className = '' }: Props) {
  const Illustration = ILLUSTRATIONS[step];
  return (
    <div className={`public-inscription-step-illustration ${className}`.trim()} aria-hidden>
      <Illustration />
    </div>
  );
}

export type { StepId };
